//
// arc-layout.ts — where everything sits on the arc
//
// Ported from `~/akaspace-web/src/hud/arcLayout.ts`, which is itself a
// translation of `Akaspace/Core/ArcMenuMath.swift`. Build 1.5, item 6.
//
// > **The numbers below were hand-placed on an iOS bench at 440pt and carry
// > their own warning in the file they came from: "These are not an
// > even-spacing rule; do not tidy them." That warning travels with them.**
//
// They are not arbitrary and they are not derivable. Someone sat with a
// phone and moved each seat until the row looked right, and an evenly
// spaced version has already been tried and looked wrong. Leave them.
//
// What AZISUR drops from the akaspace original: the toolkit tier (seven
// one-shot action seats) and the palette tier (six colour chips). Neither
// has a meaning here. Dave: *"the sub-menus that come up will have a
// different structure than akaspace. We will use our vault menu structure
// rather than the tool kit options."* The vault tier is four seats, which
// is exactly the four-per-genre shape item 7 asks for.
//
// Mirrors `Views/Arc/ArcMenuLayout.swift`.
//

/** The bench width every hand-placed number below was measured against. */
export const HAND_WIDTH = 440;
export const MAX_SCALE = 1.5;

/** How far below the bottom edge the circle's centre sits. */
export const CENTER_BELOW = 109;
export const LANE_CENTER_BELOW = 98;

export const INNER_LINE_RADIUS = 128;
export const LANE_WIDTH = 22;
export const LANE_COUNT = 3;
export const SWEEP_HALF_DEG = 70;

export const TIER1_RADIUS = 238;
export const TIER2_RADIUS = 288;
export const TIER1_SEAT = 58;
export const TIER2_SEAT = 52;
export const POLARITY_R = 14;

/** The crest rides up as the second tier opens. */
export const CREST_ABOVE_TIER1 = 306;
export const CREST_ABOVE_TIER2 = 352;
export const CREST_ART = 56;

/** Hand-placed seat positions. Do not tidy. */
export const TIER1_HAND_X = [74.2, 142.4, 220.0, 299.3, 368.4];
export const TIER2_HAND_X = [82.5, 173.2, 270.0, 360.3];

/**
 * Drag sensitivities. Zoom and pan are akaspace's, unchanged — Dave: *"the
 * zoom in and pan buttons will function the same way."* The outer lane's
 * two are new: it carried brightness and star magnitude there, and carries
 * visibility and the timeline here.
 */
export const VISIBILITY_SENS = 0.5;
export const TIMELINE_SENS = 0.004;
/** Applied as `scale *= exp(arc * ZOOM_SENS)` — build 2.0, item 8. */
export const ZOOM_SENS = 0.004;
export const PAN_SENS = 0.0028;

/** Which row of the menu is showing. */
export type ArcTier = "hidden" | "tier1" | "tier2";

export interface Layout {
  w: number;
  h: number;
  scale: number;
  cx: number;
  cy: number;
  laneCx: number;
  laneCy: number;
}

export function scaleFor(width: number): number {
  return Math.min(width / HAND_WIDTH, MAX_SCALE);
}

export function layout(w: number, h: number, userScale = 1): Layout {
  const scale = scaleFor(w) * userScale;
  return {
    w,
    h,
    scale,
    cx: w / 2,
    cy: h + CENTER_BELOW * scale,
    laneCx: w / 2,
    laneCy: h + LANE_CENTER_BELOW * scale,
  };
}

export function lineRadii(scale: number): number[] {
  return [0, 1, 2, 3].map((i) => (INNER_LINE_RADIUS + (LANE_COUNT - i) * LANE_WIDTH) * scale);
}

export function laneBounds(scale: number, i: number): { outer: number; inner: number; mid: number } {
  const r = lineRadii(scale);
  return { outer: r[i], inner: r[i + 1], mid: (r[i] + r[i + 1]) / 2 };
}

export function handX(list: number[], index: number, w: number, scale: number): number {
  return w / 2 + (list[index] - HAND_WIDTH / 2) * scale;
}

/**
 * Drops a seat onto its ring: given where it sits horizontally, find the
 * point on the circle at that x. Past the circle's own width it clamps to
 * the apex rather than returning a NaN.
 */
export function onRing(x: number, radius: number, cx: number, cy: number): { x: number; y: number } {
  const dx = x - cx;
  if (Math.abs(dx) >= radius) return { x: cx, y: cy - radius };
  return { x, y: cy - Math.sqrt(radius * radius - dx * dx) };
}

export function seat(l: Layout, i: number): { x: number; y: number } {
  return onRing(handX(TIER1_HAND_X, i, l.w, l.scale), TIER1_RADIUS * l.scale, l.cx, l.cy);
}

export function tier2Seat(l: Layout, i: number): { x: number; y: number } {
  return onRing(handX(TIER2_HAND_X, i, l.w, l.scale), TIER2_RADIUS * l.scale, l.cx, l.cy);
}

export function apex(cx: number, cy: number, r: number): { x: number; y: number } {
  return { x: cx, y: cy - r };
}

export function polarity(l: Layout): { x: number; y: number } {
  return { x: l.w / 2, y: l.h - POLARITY_R * l.scale };
}

export function crestOpen(l: Layout, tier: ArcTier = "tier1"): { x: number; y: number } {
  const above = tier === "tier2" ? CREST_ABOVE_TIER2 : CREST_ABOVE_TIER1;
  return apex(l.cx, l.cy, above * l.scale);
}

/** The angle of a point around the circle's centre, measured from the apex. */
export function phiOf(x: number, y: number, cx: number, cy: number): number {
  return Math.atan2(x - cx, cy - y);
}

export function arcPath(cx: number, cy: number, r: number): string {
  const half = (SWEEP_HALF_DEG * Math.PI) / 180;
  const x0 = cx + r * Math.sin(-half);
  const y0 = cy - r * Math.cos(-half);
  const x1 = cx + r * Math.sin(half);
  const y1 = cy - r * Math.cos(half);
  return `M ${x0.toFixed(1)} ${y0.toFixed(1)} A ${r.toFixed(1)} ${r.toFixed(1)} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`;
}

/** Top of the highest thing drawn for this tier — crest or seat, whichever wins. */
export function menuHeight(tier: ArcTier, scale: number): number {
  if (tier === "hidden") return (18 + 20) * scale;
  const fanTop = (radius: number, diameter: number) =>
    (radius - CENTER_BELOW + diameter / 2) * scale;
  const crestTop = (above: number) => (above - CENTER_BELOW) * scale;
  const tier1 = Math.max(crestTop(CREST_ABOVE_TIER1), fanTop(TIER1_RADIUS, TIER1_SEAT));
  if (tier === "tier2") {
    return Math.max(crestTop(CREST_ABOVE_TIER2), fanTop(TIER2_RADIUS, TIER2_SEAT), tier1) + 16;
  }
  return tier1 + 16;
}
